import type { ReactNode } from "react"

/**
 * Lo que se manda a la impresora: N copias de la misma pieza, repartidas en hojas.
 *
 * ⚠ **No sabe qué está imprimiendo, y ésa es la diferencia con la versión
 * anterior.** Recibía un `DatosRotulo` y llamaba a `VistaRotulo`, así que el pliego
 * —que sólo entiende de hojas y de copias— tenía dentro el catálogo entero de los
 * formatos de rotulación. El día que hizo falta imprimir un carnet no había forma de
 * reutilizarlo sin meterle un segundo `switch`. Ahora recibe la pieza ya pintada.
 *
 * ⚠ **Vive FUERA de la pantalla, no dentro de un modal ni oculto con `display:
 * none`.** Un elemento con `display: none` no se maqueta —el navegador tampoco lo
 * maqueta al imprimir—, así que el truco de esconder la versión imprimible y
 * enseñarla sólo en `@media print` no funciona si se hace con `display`. Aquí el
 * pliego se saca del flujo con posición absoluta y se le quita la visibilidad en
 * pantalla; al imprimir, la regla de `globales.css` lo devuelve al flujo y esconde el
 * resto.
 *
 * ⚠ **Se imprimen COPIAS de la misma pieza, no piezas distintas en la misma hoja.**
 * Un pliego mezclado obliga a recortar cuatro medidas diferentes de una hoja, y las
 * tijeras no siguen líneas que no están. Quien necesita cinco lomos distintos los
 * imprime en cinco tandas, que es como se hace hoy con el `.xls` y nadie se queja.
 *
 * ⚠ **Las guías de corte son un borde punteado y no una cruz de imprenta.** Esto se
 * recorta con tijera sobre una mesa, no con guillotina: una línea continua por donde
 * cortar es más útil que cuatro marcas en las esquinas.
 */
export function PliegoImpresion({
  pieza,
  copias,
  porHoja,
  guias = true,
}: {
  /**
   * La pieza ya pintada. Se repite tal cual: es el mismo elemento de React puesto N
   * veces, que React admite sin copiarlo.
   */
  pieza: ReactNode
  copias: number
  /** Cuántas caben en una hoja. Lo calcula `hoja.cabenPorHoja`. */
  porHoja: number
  /**
   * Las guías de corte.
   *
   * ⚠ **Se quitan cuando la pieza ES la hoja entera.** Una plantilla oficial ocupa
   * el A4 completo: no hay nada que recortar, y el punteado saldría pegado al borde
   * del papel o, peor, empujaría la hoja a una segunda página.
   */
  guias?: boolean
}) {
  // Se reparten en hojas para que cada una lleve su `break-after: page`.
  const hojas: number[] = []
  for (let restantes = copias; restantes > 0; restantes -= porHoja) {
    hojas.push(Math.min(porHoja, restantes))
  }

  return (
    <div className="pliego-impresion" aria-hidden={true}>
      {hojas.map((enEstaHoja, indiceHoja) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: son hojas idénticas numeradas por posición; no hay identidad que conservar
          key={indiceHoja}
          className="pliego-hoja"
        >
          {Array.from({ length: enEstaHoja }, (_, indice) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: copias idénticas de la misma pieza
              key={indice}
              style={guias ? { outline: "0.2mm dashed #bbb", outlineOffset: "1.5mm" } : undefined}
            >
              {pieza}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
